import React, { useState, useEffect } from 'react';
import { Plus, Mail, Users, BarChart3, Play, Pause, Pencil, Trash2, Eye, Workflow, GitBranch, FlaskConical, AlertTriangle, X } from 'lucide-react';
import { toast } from 'react-toastify';
import ModernSettingHeader from '@/components/settings/ModernSettingHeader';
import {
  getCampaigns,
  deleteCampaign,
  activateCampaign,
  pauseCampaign,
  DripCampaign
} from '@/actions/drip-campaigns';
import CampaignFormModal from './CampaignFormModal';
import CampaignStatsModal from './CampaignStatsModal';
import CampaignBuilderModal from './CampaignBuilderModal';

const DripCampaignsPage: React.FC = () => {
  const [campaigns, setCampaigns] = useState<DripCampaign[]>([]);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState<string>('');
  const [showCampaignModal, setShowCampaignModal] = useState(false);
  const [showBuilderModal, setShowBuilderModal] = useState(false);
  const [showStatsModal, setShowStatsModal] = useState(false);
  const [selectedCampaign, setSelectedCampaign] = useState<DripCampaign | null>(null);
  const [goLiveCampaign, setGoLiveCampaign] = useState<DripCampaign | null>(null);
  const [goingLive, setGoingLive] = useState(false);

  useEffect(() => {
    loadCampaigns();
  }, [filterStatus]);

  const loadCampaigns = async () => {
    try {
      setLoading(true);
      const response = await getCampaigns({ status: filterStatus || undefined });
      if (response.success && response.data) {
        setCampaigns(response.data);
      }
    } catch (error) {
      console.error('Error loading campaigns:', error);
      toast.error('Failed to load campaigns');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateCampaign = () => {
    setSelectedCampaign(null);
    setShowCampaignModal(true);
  };

  const handleEditCampaign = (campaign: DripCampaign) => {
    setSelectedCampaign(campaign);
    setShowCampaignModal(true);
  };

  const handleOpenVisualBuilder = (campaign?: DripCampaign) => {
    setSelectedCampaign(campaign || null);
    setShowBuilderModal(true);
  };

  const handleDeleteCampaign = async (campaignId: number, campaignName: string) => {
    if (!confirm(`Are you sure you want to delete "${campaignName}"?`)) {
      return;
    }

    try {
      await deleteCampaign(campaignId);
      toast.success('Campaign deleted successfully');
      loadCampaigns();
    } catch (error: any) {
      console.error('Error deleting campaign:', error);
      toast.error(error.response?.data?.error || 'Failed to delete campaign');
    }
  };

  const handleActivateCampaign = async (campaign: DripCampaign) => {
    // Test mode off means real customers get messaged. Confirm before sending
    // confirm_live, which the API requires for that path.
    if (!campaign.test_mode) {
      setGoLiveCampaign(campaign);
      return;
    }
    try {
      await activateCampaign(campaign.id);
      toast.success('Activated in TEST MODE — sends go to the test number only');
      loadCampaigns();
    } catch (error: any) {
      console.error('Error activating campaign:', error);
      toast.error(error.response?.data?.message || 'Failed to activate campaign');
    }
  };

  const confirmGoLive = async () => {
    if (!goLiveCampaign) return;
    setGoingLive(true);
    try {
      await activateCampaign(goLiveCampaign.id, true);
      toast.success('Campaign is LIVE — real customers will now be messaged');
      setGoLiveCampaign(null);
      loadCampaigns();
    } catch (error: any) {
      console.error('Error activating campaign:', error);
      toast.error(error.response?.data?.message || 'Failed to activate campaign');
    } finally {
      setGoingLive(false);
    }
  };

  const handlePauseCampaign = async (campaignId: number) => {
    try {
      await pauseCampaign(campaignId);
      toast.success('Campaign paused successfully');
      loadCampaigns();
    } catch (error: any) {
      console.error('Error pausing campaign:', error);
      toast.error(error.response?.data?.error || 'Failed to pause campaign');
    }
  };

  const handleViewStats = (campaign: DripCampaign) => {
    setSelectedCampaign(campaign);
    setShowStatsModal(true);
  };

  const getStatusBadge = (status: string) => {
    const badges = {
      draft: 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300',
      active: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300',
      paused: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-300',
      archived: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300',
    };
    return badges[status as keyof typeof badges] || badges.draft;
  };

  return (
    <div className="w-full">
      <ModernSettingHeader
        title="Drip Email Campaigns"
        description="Create automated email sequences to nurture your leads"
        icon={<Mail className="w-6 h-6" />} />

      <div className="p-6">

      {/* Actions Bar */}
      <div className="flex justify-between items-center mb-6">
        <div className="flex gap-2">
          <button
            onClick={() => setFilterStatus('')}
            className={`px-4 py-2 rounded-lg ${
              filterStatus === ''
                ? 'bg-blue-600 text-white'
                : 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300'
            }`}
          >
            All
          </button>
          <button
            onClick={() => setFilterStatus('active')}
            className={`px-4 py-2 rounded-lg ${
              filterStatus === 'active'
                ? 'bg-blue-600 text-white'
                : 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300'
            }`}
          >
            Active
          </button>
          <button
            onClick={() => setFilterStatus('draft')}
            className={`px-4 py-2 rounded-lg ${
              filterStatus === 'draft'
                ? 'bg-blue-600 text-white'
                : 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300'
            }`}
          >
            Draft
          </button>
          <button
            onClick={() => setFilterStatus('paused')}
            className={`px-4 py-2 rounded-lg ${
              filterStatus === 'paused'
                ? 'bg-blue-600 text-white'
                : 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300'
            }`}
          >
            Paused
          </button>
        </div>

        <div className="flex gap-2">
          <button
            onClick={handleCreateCampaign}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            <Plus className="w-5 h-5" />
            Create Campaign
          </button>
          <button
            onClick={() => handleOpenVisualBuilder()}
            className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
            title="Create with Visual Flow Builder"
          >
            <GitBranch className="w-5 h-5" />
            Visual Builder
          </button>
        </div>
      </div>

      {/* Campaigns Grid */}
      {loading ? (
        <div className="text-center py-12">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          <p className="mt-4 text-gray-600 dark:text-gray-400">Loading campaigns...</p>
        </div>
      ) : campaigns.length === 0 ? (
        <div className="text-center py-12 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
          <Mail className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
            No campaigns yet
          </h3>
          <p className="text-gray-600 dark:text-gray-400 mb-4">
            Create your first drip campaign to start nurturing leads
          </p>
          <button
            onClick={handleCreateCampaign}
            className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            <Plus className="w-5 h-5" />
            Create Your First Campaign
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {campaigns.map((campaign) => (
            <div
              key={campaign.id}
              className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6 hover:shadow-lg transition-shadow"
            >
              {/* Campaign Header */}
              <div className="flex justify-between items-start mb-4">
                <div className="flex-1">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-1">
                    {campaign.campaign_name}
                  </h3>
                  <div className="flex items-center gap-1.5 flex-wrap">
                    <span
                      className={`inline-block px-2 py-1 text-xs font-medium rounded ${getStatusBadge(
                        campaign.status
                      )}`}
                    >
                      {campaign.status.charAt(0).toUpperCase() + campaign.status.slice(1)}
                    </span>
                    {/* Otherwise an active test-mode campaign is indistinguishable from
                        a live one at a glance. */}
                    {campaign.test_mode && (
                      <span
                        className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium rounded bg-indigo-100 text-indigo-700 dark:bg-indigo-900 dark:text-indigo-300"
                        title={campaign.test_recipient_phone
                          ? `Sends go to ${campaign.test_recipient_phone} only`
                          : 'Sends go to the configured test number only'}
                      >
                        <FlaskConical className="w-3 h-3" /> Test mode
                      </span>
                    )}
                  </div>
                </div>
              </div>

              {/* Campaign Description */}
              {campaign.description && (
                <p className="text-sm text-gray-600 dark:text-gray-400 mb-4 line-clamp-2">
                  {campaign.description}
                </p>
              )}

              {/* Campaign Type & Trigger Info */}
              <div className="mb-4">
                {campaign.enrollment_type === 'auto_trigger' ? (
                  <div className="flex items-start gap-2 p-3 bg-purple-50 dark:bg-purple-900/20 rounded-lg border border-purple-200 dark:border-purple-800">
                    <Workflow className="w-4 h-4 text-purple-600 dark:text-purple-400 mt-0.5 flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <div className="text-xs font-semibold text-purple-900 dark:text-purple-100 mb-1">
                        Status Change Campaign
                      </div>
                      {campaign.auto_enrollment_trigger?.trigger_config && (
                        <div className="text-xs text-purple-700 dark:text-purple-300">
                          Triggers when status → {' '}
                          <span className="font-semibold">
                            {campaign.auto_enrollment_trigger.trigger_config.to_value}
                          </span>
                          <div className="mt-1 text-purple-600 dark:text-purple-400">
                            Single email sent automatically
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                ) : (
                  <div className="flex items-start gap-2 p-3 bg-green-50 dark:bg-green-900/20 rounded-lg border border-green-200 dark:border-green-800">
                    <Mail className="w-4 h-4 text-green-600 dark:text-green-400 mt-0.5 flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <div className="text-xs font-semibold text-green-900 dark:text-green-100 mb-1">
                        Drip Campaign
                      </div>
                      <div className="text-xs text-green-700 dark:text-green-300">
                        Multi-step email sequence
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Campaign Stats */}
              <div className="grid grid-cols-3 gap-4 mb-4 pb-4 border-b border-gray-200 dark:border-gray-700">
                <div>
                  <div className="flex items-center gap-1 text-xs text-gray-500 dark:text-gray-400 mb-1">
                    <Mail className="w-3 h-3" />
                    Steps
                  </div>
                  <div className="text-lg font-semibold text-gray-900 dark:text-white">
                    {campaign.steps?.length || 0}
                  </div>
                </div>
                <div>
                  <div className="flex items-center gap-1 text-xs text-gray-500 dark:text-gray-400 mb-1">
                    <Users className="w-3 h-3" />
                    Enrolled
                  </div>
                  <div className="text-lg font-semibold text-gray-900 dark:text-white">
                    {campaign.total_enrolled}
                  </div>
                </div>
                <div>
                  <div className="flex items-center gap-1 text-xs text-gray-500 dark:text-gray-400 mb-1">
                    <BarChart3 className="w-3 h-3" />
                    Completed
                  </div>
                  <div className="text-lg font-semibold text-gray-900 dark:text-white">
                    {campaign.total_completed}
                  </div>
                </div>
              </div>

              {/* Campaign Actions */}
              <div className="flex gap-2">
                {campaign.status === 'draft' && (
                  <button
                    onClick={() => handleActivateCampaign(campaign)}
                    className="flex-1 flex items-center justify-center gap-1 px-3 py-2 bg-green-600 text-white text-sm rounded hover:bg-green-700"
                    title="Activate Campaign"
                  >
                    <Play className="w-4 h-4" />
                    Activate
                  </button>
                )}
                {campaign.status === 'active' && (
                  <button
                    onClick={() => handlePauseCampaign(campaign.id)}
                    className="flex-1 flex items-center justify-center gap-1 px-3 py-2 bg-yellow-600 text-white text-sm rounded hover:bg-yellow-700"
                    title="Pause Campaign"
                  >
                    <Pause className="w-4 h-4" />
                    Pause
                  </button>
                )}
                {campaign.status === 'paused' && (
                  <button
                    onClick={() => handleActivateCampaign(campaign)}
                    className="flex-1 flex items-center justify-center gap-1 px-3 py-2 bg-green-600 text-white text-sm rounded hover:bg-green-700"
                    title="Resume Campaign"
                  >
                    <Play className="w-4 h-4" />
                    Resume
                  </button>
                )}
                <button
                  onClick={() => handleEditCampaign(campaign)}
                  className="flex-1 flex items-center justify-center gap-1 px-3 py-2 bg-blue-600 text-white text-sm rounded hover:bg-blue-700"
                  title="Edit Campaign (Form)"
                >
                  <Pencil className="w-4 h-4" />
                  Edit
                </button>
                {campaign.enrollment_type !== 'auto_trigger' && (
                  <button
                    onClick={() => handleOpenVisualBuilder(campaign)}
                    className="flex items-center justify-center px-3 py-2 bg-purple-600 text-white text-sm rounded hover:bg-purple-700"
                    title="Edit in Visual Builder"
                  >
                    <GitBranch className="w-4 h-4" />
                  </button>
                )}
                <button
                  onClick={() => handleViewStats(campaign)}
                  className="flex items-center justify-center px-3 py-2 bg-gray-600 text-white text-sm rounded hover:bg-gray-700"
                  title="View Statistics"
                >
                  <Eye className="w-4 h-4" />
                </button>
                {campaign.status === 'draft' && (
                  <button
                    onClick={() => handleDeleteCampaign(campaign.id, campaign.campaign_name)}
                    className="flex items-center justify-center px-3 py-2 bg-red-600 text-white text-sm rounded hover:bg-red-700"
                    title="Delete Campaign"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Campaign Form Modal */}
      {showCampaignModal && (
        <CampaignFormModal
          isOpen={showCampaignModal}
          onClose={() => {
            setShowCampaignModal(false);
            setSelectedCampaign(null);
          }}
          campaign={selectedCampaign}
          onSuccess={loadCampaigns}
        />
      )}

      {/* Campaign Stats Modal */}
      {showStatsModal && selectedCampaign && (
        <CampaignStatsModal
          isOpen={showStatsModal}
          onClose={() => {
            setShowStatsModal(false);
            setSelectedCampaign(null);
          }}
          campaign={selectedCampaign}
        />
      )}

      {/* Go-live confirmation. A sent message cannot be recalled, so leaving test mode
          is confirmed explicitly — the API also refuses without confirm_live. */}
      {goLiveCampaign && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl w-full max-w-md">
            <div className="flex items-start gap-3 px-5 py-4 border-b border-gray-100 dark:border-gray-700">
              <div className="p-2 rounded-full bg-amber-100 dark:bg-amber-900 flex-shrink-0">
                <AlertTriangle className="w-5 h-5 text-amber-600 dark:text-amber-300" />
              </div>
              <div className="flex-1">
                <h3 className="text-base font-semibold text-gray-900 dark:text-white">
                  Activate for real customers?
                </h3>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5 truncate">
                  {goLiveCampaign.campaign_name}
                </p>
              </div>
              <button
                onClick={() => setGoLiveCampaign(null)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-5 space-y-3">
              <p className="text-sm text-gray-700 dark:text-gray-300">
                Test mode is <strong>off</strong>. Activating this campaign will send real
                messages to every customer who matches its trigger.
              </p>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Sent messages cannot be recalled. If you have not walked this flow through
                in test mode yet, turn test mode on first.
              </p>
            </div>
            <div className="flex items-center justify-end gap-2 px-5 py-4 border-t border-gray-100 dark:border-gray-700">
              <button
                onClick={() => setGoLiveCampaign(null)}
                className="px-4 py-2 text-sm rounded-lg border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
              >
                Cancel
              </button>
              <button
                onClick={confirmGoLive}
                disabled={goingLive}
                className="px-4 py-2 text-sm rounded-lg bg-red-600 text-white hover:bg-red-700 disabled:opacity-60"
              >
                {goingLive ? 'Activating…' : 'Yes, go live'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Visual Campaign Builder Modal */}
      {showBuilderModal && (
        <CampaignBuilderModal
          isOpen={showBuilderModal}
          onClose={() => {
            setShowBuilderModal(false);
            setSelectedCampaign(null);
          }}
          campaign={selectedCampaign}
          onSuccess={loadCampaigns}
        />
      )}
      </div>
      </div>
  );
};

export default DripCampaignsPage;
